import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedDocumentUploadURLForRespondentsRPC implements
  LeoRPC<GetPresignedDocumentUploadURLForRespondentsRPC.Request, GetPresignedDocumentUploadURLForRespondentsRPC.Response, GetPresignedDocumentUploadURLForRespondentsRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDocumentUploadURLForRespondentsRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDocumentUploadURLForRespondentsRPC.Response, GetPresignedDocumentUploadURLForRespondentsRPC.Errors.Errors>>;
}

export namespace GetPresignedDocumentUploadURLForRespondentsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileAttributes: FileAttributesDTO;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDocumentUploadURLForRespondentsRPCRequest: boolean = true;
  
    readonly fileAttributes: FileAttributes;
    readonly projectId: LeoUUID;
  
  
    constructor(
    fileAttributes: FileAttributes, 
    projectId: LeoUUID
    ) {
      this.fileAttributes = fileAttributes;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        fileAttributes,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileAttributes: this.fileAttributes.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForRespondentsRPC(fileAttributes='${this.fileAttributes}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDocumentUploadURLForRespondentsResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForRespondents(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    RespondentUpdateInProgress = "RESPONDENT_UPDATE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface RespondentUpdateInProgressDTO {
      code: string;
    }
    
    export class RespondentUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isRespondentUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): RespondentUpdateInProgressDTO {
        const dto: RespondentUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentUpdateInProgress.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForRespondentsRPC.RESPONDENT_UPDATE_IN_PROGRESS()`;
    };
    

    export type Errors = RespondentUpdateInProgress;
    export type ErrorsDTO = RespondentUpdateInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.RespondentUpdateInProgress:
          return new RespondentUpdateInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedDocumentUploadURLForRespondentsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDocumentUploadURLForRespondentsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDocumentUploadURLForRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseRespondentUpdateInProgressError extends GetPresignedDocumentUploadURLForRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateInProgressError";
  }
}

